/**
 * Self-test suite for cross-device draft persistence.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/DraftSelfTest.swift` case-for-case
 * and adds web-specific cases (unicode scalar counting, IndexedDB
 * persistence across store instances, decode-side size cap enforcement).
 *
 * All cases use isolated IndexedDB database names so the suite is safe to
 * run on every dev launch without polluting production state.
 *
 * Registered with the global SelfTestRunner singleton at module load time.
 * The runner auto-discovers this file via Vite `import.meta.glob` in
 * `selftest/index.ts` (matches `**\/*SelfTest.ts`).
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  DraftCodec,
  DraftCodecError,
  countCodePoints,
  makeDraftPayload,
  DRAFT_SCHEMA_VERSION,
} from "./DraftMessage";
import { DraftStore } from "./DraftStore";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

let dbCounter = 0;

/** Each call returns a DraftStore backed by a fresh, isolated IDB database. */
function freshStore(): DraftStore {
  dbCounter += 1;
  return new DraftStore(`kaether:drafts:selftest:${dbCounter}:${Date.now()}`);
}

/** base64url-encode a UTF-8 string (for crafting synthetic payloads in tests). */
function b64url(s: string): string {
  const bytes = new TextEncoder().encode(s);
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]!);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// ---------------------------------------------------------------------------
// Suite registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Drafts (per-thread persistence)",
  isVerified: true,
  run: async () =>
    buildSuiteResult(
      await Promise.all([
        // Codec cases
        testCodecRoundTripNonEmpty(),
        testCodecRoundTripEmpty(),
        testIsDraftDetection(),
        testTryDecodeReturnsNullForUserMessage(),
        testMalformedBase64Rejected(),
        testUnsupportedSchemaRejected(),
        testEncodeRejectsOversizeText(),
        testDecodeRejectsOversizeDecoded(),
        testUnicodeScalarBoundary(),

        // Store mechanics
        testSetLocalRoundTrip(),
        testSetLocalSameTsNoOp(),
        testNewerTsWins(),
        testOlderTsRejected(),
        testEmptyTextClearsRecord(),
        testResetAll(),
        testPersistenceAcrossInstances(),
        testAllDraftsOrderedByObservedAt(),
        testClearEquivalentToEmptySetLocal(),

        // Multi-channel isolation
        testDraftInOneChannelDoesNotAffectAnother(),
        testClearingOneChannelLeavesAnother(),
        testAllDraftsReturnsBoth(),

        // Server-attack resistance
        testHostilePayloadChannelIdRoutesToItsOwnChannel(),
        testStaleClearDoesNotWipeNewerDraft(),
        testEncodeEnforcesSizeCap(),
      ]),
    ),
});

// ---------------------------------------------------------------------------
// Codec cases
// ---------------------------------------------------------------------------

async function testCodecRoundTripNonEmpty(): Promise<TestCase> {
  const name = "codec round-trips non-empty draft";
  try {
    const p = makeDraftPayload("chan-1", "hello world", 1_700_000_000);
    const encoded = DraftCodec.encode(p);
    const decoded = DraftCodec.decode(encoded);
    if (
      decoded.schema === p.schema &&
      decoded.channelId === p.channelId &&
      decoded.text === p.text &&
      decoded.ts === p.ts
    ) {
      return pass(name);
    }
    return fail(name, `mismatch — got ${JSON.stringify(decoded)}, want ${JSON.stringify(p)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testCodecRoundTripEmpty(): Promise<TestCase> {
  const name = "codec round-trips empty-text clear-draft signal";
  try {
    const p = makeDraftPayload("chan-1", "", 1_700_000_001);
    const encoded = DraftCodec.encode(p);
    const decoded = DraftCodec.decode(encoded);
    if (decoded.text === "" && decoded.channelId === "chan-1" && decoded.ts === 1_700_000_001) {
      return pass(name);
    }
    return fail(name, `mismatch — got ${JSON.stringify(decoded)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testIsDraftDetection(): Promise<TestCase> {
  const name = "isDraft distinguishes from chat / envelope / receipt / tombstone / reply / pin";
  try {
    const p = makeDraftPayload("chan-1", "hi", 1);
    const encoded = DraftCodec.encode(p);
    const positive = DraftCodec.isDraft(encoded);
    const negUserMessage = DraftCodec.isDraft("hello world");
    const negEnvelope = DraftCodec.isDraft("kaether:e2ee:v1:foo");
    const negReceipt = DraftCodec.isDraft("kaether:receipt:v1:abc");
    const negTombstone = DraftCodec.isDraft("kaether:tombstone:v1:abc");
    const negReply = DraftCodec.isDraft("kaether:reply:v1:abc");
    const negPin = DraftCodec.isDraft("kaether:pin:v1:abc");
    if (
      positive &&
      !negUserMessage &&
      !negEnvelope &&
      !negReceipt &&
      !negTombstone &&
      !negReply &&
      !negPin
    ) {
      return pass(name);
    }
    return fail(
      name,
      `pos=${positive} user=${negUserMessage} env=${negEnvelope} rcpt=${negReceipt} tomb=${negTombstone} reply=${negReply} pin=${negPin}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testTryDecodeReturnsNullForUserMessage(): Promise<TestCase> {
  const name = "tryDecode returns null for user messages";
  try {
    const r = DraftCodec.tryDecode("just a chat message");
    if (r === null) return pass(name);
    return fail(name, `expected null, got ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, `threw unexpectedly: ${e}`);
  }
}

async function testMalformedBase64Rejected(): Promise<TestCase> {
  const name = "malformed base64 in draft is rejected";
  const bad = DraftCodec.PREFIX + "@@@not-base64@@@";
  try {
    DraftCodec.decode(bad);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof DraftCodecError && e.kind.tag === "malformed") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testUnsupportedSchemaRejected(): Promise<TestCase> {
  const name = "unsupported schema version is rejected";
  const json = JSON.stringify({ schema: 99, channelId: "c", text: "hi", ts: 1 });
  const payload = DraftCodec.PREFIX + b64url(json);
  try {
    DraftCodec.decode(payload);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (
      e instanceof DraftCodecError &&
      e.kind.tag === "unsupportedSchema" &&
      e.kind.version === 99
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testEncodeRejectsOversizeText(): Promise<TestCase> {
  const name = "encode rejects oversize text (>10_000 code points) with tooLarge";
  const oversize = "x".repeat(DraftCodec.MAX_DRAFT_LENGTH + 1);
  try {
    DraftCodec.encode(makeDraftPayload("c", oversize, 1));
    return fail(name, "encode did not throw");
  } catch (e) {
    if (
      e instanceof DraftCodecError &&
      e.kind.tag === "tooLarge" &&
      e.kind.count === DraftCodec.MAX_DRAFT_LENGTH + 1
    ) {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testDecodeRejectsOversizeDecoded(): Promise<TestCase> {
  const name = "decode rejects maliciously-crafted ciphertext yielding >10_000 code points";
  // Craft a raw JSON with oversize text and inject it directly as base64url,
  // bypassing encode (which would reject it). This simulates a server that
  // crafts the ciphertext itself rather than going through the codec.
  const oversizeText = "a".repeat(DraftCodec.MAX_DRAFT_LENGTH + 1);
  const raw = JSON.stringify({
    schema: DRAFT_SCHEMA_VERSION,
    channelId: "c",
    text: oversizeText,
    ts: 1,
  });
  const crafted = DraftCodec.PREFIX + b64url(raw);
  try {
    DraftCodec.decode(crafted);
    return fail(name, "decode did not throw");
  } catch (e) {
    if (e instanceof DraftCodecError && e.kind.tag === "tooLarge") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}

async function testUnicodeScalarBoundary(): Promise<TestCase> {
  const name = "unicode scalar boundary: 9_999-codepoint ZWJ emoji string is accepted, 10_001 is rejected";
  // 🤦‍♂️ is U+1F926 U+200D U+2642 U+FE0F — four code points, but
  // String.length would count it as 5 (two surrogates + ZWJ + ♂ + VS16).
  // countCodePoints must use [...str].length.
  const emoji = "🤦‍♂️"; // 4 code points
  const emojiLen = countCodePoints(emoji);
  // Build a string that is exactly 9_999 code points using the emoji repeated
  // as many times as fits, then padded with "a" to hit exactly 9_999.
  const repeatCount = Math.floor(9_999 / emojiLen);
  const remainder = 9_999 - repeatCount * emojiLen;
  const at9999 = emoji.repeat(repeatCount) + "a".repeat(remainder);
  const cpAt9999 = countCodePoints(at9999);

  if (cpAt9999 !== 9_999) {
    return fail(name, `countCodePoints helper returned ${cpAt9999} for 9_999-cp string`);
  }

  try {
    // 9_999 chars must encode fine
    DraftCodec.encode(makeDraftPayload("c", at9999, 1));
  } catch (e) {
    return fail(name, `9_999-cp string was rejected: ${e}`);
  }

  // 10_001-char ASCII string must be rejected
  const over = "a".repeat(DraftCodec.MAX_DRAFT_LENGTH + 1);
  try {
    DraftCodec.encode(makeDraftPayload("c", over, 1));
    return fail(name, "10_001-char ASCII string was accepted");
  } catch (e) {
    if (e instanceof DraftCodecError && e.kind.tag === "tooLarge") {
      return pass(name);
    }
    return fail(name, `wrong error for 10_001-char string: ${e}`);
  }
}

// ---------------------------------------------------------------------------
// Store mechanics cases
// ---------------------------------------------------------------------------

async function testSetLocalRoundTrip(): Promise<TestCase> {
  const name = "setLocal then draft(channelId) returns the same text";
  try {
    const s = freshStore();
    await s.setLocal("c1", "hello", 100);
    const r = await s.draft("c1");
    if (r?.text === "hello" && r.channelId === "c1" && r.ts === 100) {
      return pass(name);
    }
    return fail(name, `got ${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testSetLocalSameTsNoOp(): Promise<TestCase> {
  const name = "setLocal with same ts is a no-op (first writer wins on tie)";
  try {
    const s = freshStore();
    const firstChange = await s.setLocal("c1", "hi", 100, 1000);
    const firstObs = (await s.draft("c1"))?.observedAt;
    const secondChange = await s.setLocal("c1", "hi", 100, 999_000);
    const secondObs = (await s.draft("c1"))?.observedAt;
    if (firstChange && !secondChange && firstObs === 1000 && secondObs === 1000) {
      return pass(name);
    }
    return fail(
      name,
      `firstChange=${firstChange} secondChange=${secondChange} firstObs=${firstObs} secondObs=${secondObs}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testNewerTsWins(): Promise<TestCase> {
  const name = "newer ts overwrites older draft";
  try {
    const s = freshStore();
    await s.setLocal("c1", "old", 100);
    const changed = await s.setLocal("c1", "new", 200);
    const r = await s.draft("c1");
    if (changed && r?.text === "new" && r.ts === 200) {
      return pass(name);
    }
    return fail(name, `changed=${changed} got=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testOlderTsRejected(): Promise<TestCase> {
  const name = "older ts is rejected (no resurrect)";
  try {
    const s = freshStore();
    await s.setLocal("c1", "current", 200);
    const changed = await s.setLocal("c1", "stale", 100);
    const r = await s.draft("c1");
    if (!changed && r?.text === "current" && r.ts === 200) {
      return pass(name);
    }
    return fail(name, `changed=${changed} got=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testEmptyTextClearsRecord(): Promise<TestCase> {
  const name = "empty-text apply clears the draft (allDrafts returns no entry)";
  try {
    const s = freshStore();
    await s.setLocal("c1", "hello", 100);
    const cleared = await s.setLocal("c1", "", 200);
    const r = await s.draft("c1");
    const all = await s.allDrafts();
    const hasEntry = all.some((d) => d.channelId === "c1");
    if (cleared && r === undefined && !hasEntry) {
      return pass(name);
    }
    return fail(name, `cleared=${cleared} draft=${JSON.stringify(r)} hasEntry=${hasEntry}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testResetAll(): Promise<TestCase> {
  const name = "resetAll() drops every draft";
  try {
    const s = freshStore();
    await s.setLocal("c1", "a", 1);
    await s.setLocal("c2", "b", 1);
    await s.resetAll();
    const c1 = await s.draft("c1");
    const c2 = await s.draft("c2");
    if (c1 === undefined && c2 === undefined) {
      return pass(name);
    }
    return fail(name, `c1=${JSON.stringify(c1)} c2=${JSON.stringify(c2)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testPersistenceAcrossInstances(): Promise<TestCase> {
  const name = "draft state persists across store instances (write → reopen DB → state preserved)";
  try {
    const dbName = `kaether:drafts:selftest:persist:${Date.now()}`;
    const writer = new DraftStore(dbName);
    await writer.setLocal("c1", "persisted", 100);

    // Open a fresh instance pointing at the same DB name
    const reader = new DraftStore(dbName);
    const recovered = await reader.draft("c1");
    if (recovered?.text === "persisted" && recovered.ts === 100) {
      return pass(name);
    }
    return fail(name, `recovered=${JSON.stringify(recovered)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testAllDraftsOrderedByObservedAt(): Promise<TestCase> {
  const name = "allDrafts() orders by observedAt descending";
  try {
    const s = freshStore();
    await s.setLocal("c1", "first",  1, 100_000);
    await s.setLocal("c2", "second", 1, 200_000);
    await s.setLocal("c3", "third",  1, 150_000);
    const all = await s.allDrafts();
    if (
      all.length === 3 &&
      all[0]?.channelId === "c2" &&
      all[1]?.channelId === "c3" &&
      all[2]?.channelId === "c1"
    ) {
      return pass(name);
    }
    return fail(name, `got=${all.map((d) => d.channelId).join(",")}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testClearEquivalentToEmptySetLocal(): Promise<TestCase> {
  const name = "clear() is equivalent to setLocal with empty text";
  try {
    const s1 = freshStore();
    const s2 = freshStore();
    await s1.setLocal("c1", "hello", 100);
    await s2.setLocal("c1", "hello", 100);
    const viaClear      = await s1.clear("c1", 200);
    const viaEmptyLocal = await s2.setLocal("c1", "", 200);
    const d1 = await s1.draft("c1");
    const d2 = await s2.draft("c1");
    if (viaClear === viaEmptyLocal && d1 === undefined && d2 === undefined) {
      return pass(name);
    }
    return fail(
      name,
      `viaClear=${viaClear} viaEmptyLocal=${viaEmptyLocal} d1=${JSON.stringify(d1)} d2=${JSON.stringify(d2)}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// Multi-channel isolation cases
// ---------------------------------------------------------------------------

async function testDraftInOneChannelDoesNotAffectAnother(): Promise<TestCase> {
  const name = "draft in channel A does not affect channel B";
  try {
    const s = freshStore();
    await s.setLocal("c1", "in-c1", 1);
    const r1 = await s.draft("c1");
    const r2 = await s.draft("c2");
    if (r1?.text === "in-c1" && r2 === undefined) {
      return pass(name);
    }
    return fail(name, `c1=${JSON.stringify(r1)} c2=${JSON.stringify(r2)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testClearingOneChannelLeavesAnother(): Promise<TestCase> {
  const name = "clearing channel A leaves channel B intact";
  try {
    const s = freshStore();
    await s.setLocal("c1", "in-c1", 1);
    await s.setLocal("c2", "in-c2", 1);
    await s.clear("c1", 2);
    const r1 = await s.draft("c1");
    const r2 = await s.draft("c2");
    if (r1 === undefined && r2?.text === "in-c2") {
      return pass(name);
    }
    return fail(name, `c1=${JSON.stringify(r1)} c2=${JSON.stringify(r2)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testAllDraftsReturnsBoth(): Promise<TestCase> {
  const name = "allDrafts returns both when both channels have content";
  try {
    const s = freshStore();
    await s.setLocal("c1", "in-c1", 1, 100_000);
    await s.setLocal("c2", "in-c2", 1, 200_000);
    const all = await s.allDrafts();
    const channels = new Set(all.map((d) => d.channelId));
    if (all.length === 2 && channels.has("c1") && channels.has("c2")) {
      return pass(name);
    }
    return fail(name, `got=${all.map((d) => d.channelId).join(",")}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

// ---------------------------------------------------------------------------
// Server-attack resistance cases
// ---------------------------------------------------------------------------

async function testHostilePayloadChannelIdRoutesToItsOwnChannel(): Promise<TestCase> {
  const name =
    "hostile payload routes to its own channelId, never the receiver's expected one";
  try {
    const s = freshStore();
    await s.setLocal("expected-channel", "the real draft", 100);
    // Attacker's payload targets "different-channel" — the store must apply
    // it there and leave "expected-channel" untouched.
    await s.apply({ schema: 1, channelId: "different-channel", text: "ATTACKER", ts: 999 });
    const real     = await s.draft("expected-channel");
    const attacker = await s.draft("different-channel");
    if (
      real?.text === "the real draft" &&
      attacker?.text === "ATTACKER" &&
      attacker?.channelId === "different-channel"
    ) {
      return pass(name);
    }
    return fail(
      name,
      `real=${JSON.stringify(real)} attacker=${JSON.stringify(attacker)}`,
    );
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testStaleClearDoesNotWipeNewerDraft(): Promise<TestCase> {
  const name = "stale clear (empty text, older ts) does not wipe newer draft";
  try {
    const s = freshStore();
    await s.setLocal("c1", "fresh draft", 200);
    // Replay of an old "clear" event — must be dropped.
    const changed = await s.apply({ schema: 1, channelId: "c1", text: "", ts: 100 });
    const r = await s.draft("c1");
    if (!changed && r?.text === "fresh draft" && r.ts === 200) {
      return pass(name);
    }
    return fail(name, `changed=${changed} got=${JSON.stringify(r)}`);
  } catch (e) {
    return fail(name, String(e));
  }
}

async function testEncodeEnforcesSizeCap(): Promise<TestCase> {
  const name = "encode enforces the 10_000 code-point draft cap";
  const atLimit   = "a".repeat(DraftCodec.MAX_DRAFT_LENGTH);
  const overLimit = "a".repeat(DraftCodec.MAX_DRAFT_LENGTH + 1);
  try {
    DraftCodec.encode(makeDraftPayload("c", atLimit, 1));
  } catch (e) {
    return fail(name, `10_000-char text was rejected: ${e}`);
  }
  try {
    DraftCodec.encode(makeDraftPayload("c", overLimit, 1));
    return fail(name, "10_001-char text was accepted");
  } catch (e) {
    if (e instanceof DraftCodecError && e.kind.tag === "tooLarge") {
      return pass(name);
    }
    return fail(name, `wrong error: ${e}`);
  }
}
